// navSound — soft "bubble pop" chime for the bottom-nav tab change.
//
// User feedback on previous iterations:
//   v1 (C major arpeggio):  "doesn't feel like the jiggle is in the sound"
//   v2 (high wobble FM):    "sounds like someone is screaming"
//                           — the ±180Hz wobble at 9Hz on an 880Hz
//                             carrier was the audible equivalent of a
//                             frantic vibrato. Removed entirely.
//
// v3 (this): a soft, descending pitch sweep — the classic "bubble pop"
// (think Skype incoming-message tone). The downward sweep feels
// satisfying and round rather than alarming, AND it still embodies
// the spring's settling motion: the spring's overshoot finishes by
// gently coming to rest, and the pitch comes to rest at a low note
// the same way.
//
// Composition (each invocation, 1 oscillator + 1 filter + 1 env):
//   1. CARRIER — sine that sweeps from 700Hz down to 220Hz over
//      ~110ms. Sine wave keeps it pure (no harsh harmonics).
//   2. LOW-PASS FILTER — frequency sweeps from 2kHz down to 500Hz
//      in lockstep with the carrier. Q = 4 adds a subtle resonance
//      "blub" character that says "watery / round" rather than
//      "tone".
//   3. ENVELOPE — 6ms attack (no click), 160ms exponential decay.
//      Total chime length ~180ms — quick, contained, not lingering.
//
// Master gain dropped to 0.14 — even gentler than before. The
// downward-sweep shape carries enough character that volume can
// be subdued without losing presence.

let _ctx: AudioContext | null = null;
let _enabled = true;

const F_START_HZ        = 700;     // bubble forms at this pitch…
const F_END_HZ          = 220;     // …and pops down to here
const SWEEP_LEN_S       = 0.11;    // pitch glide duration
const FILTER_START_HZ   = 2000;
const FILTER_END_HZ     = 500;
const FILTER_Q          = 4;       // resonance for the "blub" feel
const ENV_LEN_S         = 0.18;
const MASTER_GAIN       = 0.14;

function ctx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!_ctx) {
    const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) return null;
    _ctx = new AC();
  }
  if (_ctx && _ctx.state === 'suspended') {
    _ctx.resume().catch(() => {});
  }
  return _ctx;
}

/**
 * Browsers gate AudioContext playback behind a user gesture. Call
 * this once on app boot from any pointer/key handler so the
 * context is "warm" by the time the user taps a nav item.
 *
 * Idempotent — safe to call from a useEffect on every mount.
 */
let _unlockInstalled = false;
export function installNavSoundUnlock() {
  if (_unlockInstalled || typeof document === 'undefined') return;
  _unlockInstalled = true;
  const handler = () => {
    const c = ctx();
    if (c && c.state === 'suspended') c.resume().catch(() => {});
    document.removeEventListener('pointerdown', handler);
    document.removeEventListener('keydown', handler);
    document.removeEventListener('touchstart', handler);
  };
  document.addEventListener('pointerdown', handler, { passive: true });
  document.addEventListener('keydown', handler);
  document.addEventListener('touchstart', handler, { passive: true });
}

export function setNavSoundEnabled(on: boolean) { _enabled = on; }
export function isNavSoundEnabled() { return _enabled; }

/**
 * Play the bubble-pop chime. Cheap to call (one short oscillator,
 * one biquad filter, one gain — all torn down via stop() schedule).
 */
export function playNavTap() {
  if (!_enabled) return;
  const c = ctx();
  if (!c) return;
  const t0 = c.currentTime;

  const master = c.createGain();
  master.gain.value = MASTER_GAIN;
  master.connect(c.destination);

  // ── Carrier with downward pitch sweep ─────────────────────
  // Exponential ramp on frequency = perceptually-linear pitch
  // glide; it feels like a single smooth slide from high to low.
  const carrier = c.createOscillator();
  carrier.type = 'sine';
  carrier.frequency.setValueAtTime(F_START_HZ, t0);
  carrier.frequency.exponentialRampToValueAtTime(F_END_HZ, t0 + SWEEP_LEN_S);

  // ── Lowpass filter that sweeps with the carrier ───────────
  // Tracks the pitch sweep so the resonance peak stays just
  // above the carrier's current frequency. The Q of 4 gives
  // that "wet / blubby" colouring you hear in real bubble pops.
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(FILTER_START_HZ, t0);
  filter.frequency.exponentialRampToValueAtTime(FILTER_END_HZ, t0 + SWEEP_LEN_S);
  filter.Q.value = FILTER_Q;

  // ── Amplitude envelope ────────────────────────────────────
  // 6ms attack (no click), exponential decay over ~170ms.
  const env = c.createGain();
  env.gain.setValueAtTime(0, t0);
  env.gain.linearRampToValueAtTime(1, t0 + 0.006);
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + ENV_LEN_S);

  carrier.connect(filter).connect(env).connect(master);
  carrier.start(t0);
  carrier.stop(t0 + ENV_LEN_S + 0.02);
}
